import { Link } from 'react-router-dom';
import { Media } from '../ui/Media';
import { Icon } from '../ui/Icon';
import { cn, useToasts } from '../ui/kit';
import { useBoutique } from '../state/boutique';
import { eur, poidsParDefaut, prixAffiche, prixPour } from '../data/repository';
import type { Produit } from '../data/mock-data';

/**
 * CARTE PRODUIT — dimensions constantes, révélation sans saut.
 *
 * Le défaut relevé par la revue (point 9) : un bouton « Plus » qui apparaissait
 * au survol en poussant la carte de quelques pixels, et toute la grille sautait.
 * Réponse : la zone d'action a une **hauteur fixe** ; au survol, le bouton rond
 * s'élargit en pastille « Ajouter » **dans ce même emplacement**, en position
 * absolue, sans toucher au flux. Au clavier, le bouton est toujours là : la
 * révélation est un agrément, pas un accès.
 *
 * Trois lignes de texte au maximum (nom, prix, estimation) : deux cartes voisines
 * ont toujours la même hauteur, quel que soit le nom du produit.
 */
export function CarteProduit({ produit, prioritaire }: { produit: Produit; prioritaire?: boolean }) {
  const { ajouter, ouvrirPanier, favoris, basculerFavori, mode, boutiqueId } = useBoutique();
  const { annoncer } = useToasts();
  const favori = favoris.includes(produit.id);
  const prix = prixAffiche(produit);
  const poids = poidsParDefaut(produit);
  const estimation = prixPour(produit, poids);
  const indisponible = mode === 'PICKUP' && !produit.disponibleEn.includes(boutiqueId);

  const ajouterVite = () => {
    ajouter(produit, { poidsG: poids });
    annoncer({
      texte: `${produit.nom} ajouté au panier — ${poids >= 1000 ? `${(poids / 1000).toFixed(2).replace('.', ',').replace(',00', '')} kg` : `${poids} g`} estimés.`,
      action: { libelle: 'Modifier', faire: () => ouvrirPanier(true) },
    });
  };

  return (
    <article className="carte group">
      <div className="carte__media">
        <Link to={`/produit/${produit.slug}`} className="block" tabIndex={-1} aria-hidden="true">
          <Media src={produit.photos[0]?.url} alt={produit.photos[0]?.alt ?? produit.nom}
            ratio="aspect-[4/3]" teinte={teinte(produit.famille)} priorite={prioritaire}
            className="transition-transform duration-500 ease-soft group-hover:scale-[1.03]" />
        </Link>

        {/* Étiquettes : deux au maximum, jamais superposées. */}
        <div className="pointer-events-none absolute left-2.5 top-2.5 flex max-w-[70%] flex-wrap gap-1.5">
          {produit.halal && (
            <span className="rounded-full bg-surface/92 px-2 py-0.5 text-[0.625rem] font-semibold text-encre-900 backdrop-blur">
              Halal
            </span>
          )}
          {produit.maturationJours != null && produit.maturationJours > 0 && (
            <span className="rounded-full bg-surface/92 px-2 py-0.5 text-[0.625rem] font-semibold text-encre-900 backdrop-blur">
              {produit.maturationJours} j
            </span>
          )}
          {produit.tags.includes('fait_maison') && (
            <span className="rounded-full bg-surface/92 px-2 py-0.5 text-[0.625rem] font-semibold text-encre-900 backdrop-blur">
              Maison
            </span>
          )}
        </div>

        <button type="button" onClick={() => basculerFavori(produit.id)}
          aria-pressed={favori}
          aria-label={favori ? `Retirer ${produit.nom} des favoris` : `Mettre ${produit.nom} en favori`}
          className={cn('absolute right-2.5 top-2.5 grid h-11 w-11 place-items-center rounded-full backdrop-blur transition',
            favori ? 'bg-wine text-white' : 'bg-surface/85 text-encre-600 hover:bg-surface hover:text-wine')}>
          <Icon name="heart" size={16} filled={favori} />
        </button>

        {indisponible && (
          <p className="absolute inset-x-2.5 bottom-2.5 rounded-lg bg-surface/92 px-2 py-1 text-center text-[0.625rem] font-medium text-encre-600 backdrop-blur">
            Retrait : Sannois uniquement
          </p>
        )}
      </div>

      {/* ------------------------------------------------------- texte */}
      <div className="carte__corps">
        <p className="text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-encre-500">
          {produit.familleNom}
        </p>
        <h3 className="mt-1 font-display text-[1.0625rem] leading-snug text-encre-900">
          <Link to={`/produit/${produit.slug}`} className="transition-colors hover:text-wine">
            <span className="line-clamp-2">{produit.nom}</span>
          </Link>
        </h3>

        <div className="mt-auto pt-2">
          <p className="flex items-baseline gap-1.5 text-[0.9375rem] font-semibold text-encre-900">
            <span className="tabular-nums">{prix.valeur}</span>
            {prix.unite && <span className="text-[0.6875rem] font-normal text-encre-500">{prix.unite}</span>}
          </p>
          <p className="mt-0.5 h-[1.15rem] text-[0.6875rem] tabular-nums text-encre-500">
            {produit.modele === 'AU_KG'
              ? `≈ ${eur(estimation)} pour ${(poids / 1000).toFixed(2).replace('.', ',').replace(',00', '')} kg`
              : produit.poidsG ? `${(produit.poidsG / 1000).toFixed(1).replace('.', ',')} kg au total` : ''}
          </p>
        </div>
      </div>

      {/* -------------------------- zone d'action à hauteur fixe (point 9) */}
      <div className="carte__actions">
        <Link to={`/produit/${produit.slug}`}
          className="absolute inset-y-0 left-0 right-14 flex items-center text-[0.75rem] font-medium text-encre-500 underline-offset-4 hover:underline">
          Voir la fiche
        </Link>
        <button type="button" onClick={ajouterVite}
          aria-label={`Ajouter ${produit.nom} au panier`}
          className="group/btn absolute right-0 top-0 flex h-11 items-center justify-center gap-1.5 rounded-full bg-encre-900 px-3 text-lin-50 shadow-soft transition-[padding,background-color] duration-300 ease-soft hover:bg-wine hover:px-4">
          <Icon name="plus" size={17} />
          <span className="max-w-0 overflow-hidden whitespace-nowrap text-[0.8125rem] font-semibold opacity-0 transition-[max-width,opacity] duration-300 ease-soft group-hover/btn:max-w-[7rem] group-hover/btn:opacity-100 group-focus-visible/btn:max-w-[7rem] group-focus-visible/btn:opacity-100">
            Ajouter
          </span>
        </button>
      </div>
    </article>
  );
}

/** La teinte du visuel suit le rayon : le catalogue reste coloré, mais cohérent. */
function teinte(famille: string): string {
  const map: Record<string, string> = {
    boeuf: 'wine', veau: 'blush', agneau: 'sand', 'canard-charcuterie': 'clay',
    volailles: 'olive', 'preparations-maison': 'wine', 'mechoui-receptions': 'clay',
    'colis-coffrets': 'sand', 'triperie-abats': 'slate', 'epicerie-fine': 'olive',
  };
  return map[famille] ?? 'sand';
}

/** Nom historique, conservé pour les écrans qui l'importent. */
export const ProductCard = CarteProduit;
